/**
 * Utility functions for date formatting and manipulation
 * Used across ProviderPage components
 */

/**
 * Creates a date key from year, month (0-indexed), and day
 * Format: YYYY-M-D (used for internal state)
 */
export const createDateKey = (year, month, day) => {
  return `${year}-${month}-${day}`;
};

/**
 * Creates a formatted date string for API calls
 * Format: YYYY-MM-DD
 */
export const createFormattedDate = (year, month, day) => {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Parses a date key (YYYY-M-D) and returns formatted date (YYYY-MM-DD)
 */
export const dateKeyToFormattedDate = (dateKey) => {
  const [year, month, day] = dateKey.split('-');
  return createFormattedDate(parseInt(year), parseInt(month), parseInt(day));
};

/**
 * Parses a date key and returns an object with year, month, day
 */
export const parseDateKey = (dateKey) => {
  const [year, month, day] = dateKey.split('-');
  return {
    year: parseInt(year),
    month: parseInt(month),
    day: parseInt(day)
  };
};

/**
 * Creates a date key from a Date object
 */
export const dateObjectToKey = (date) => {
  return createDateKey(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * Gets the number of days in a month
 */
export const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const days = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }

  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  return days;
};

/**
 * Checks if a given year, month, and day represent a date in the past
 * (before today, local time)
 */
export const isPastDate = (year, month, day) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  const targetDate = new Date(year, month, day);
  return targetDate < today;
};
